// Strava OAuth and API service

export interface StravaAthlete {
  id: number
  username: string
  firstname: string
  lastname: string
  profile: string
  city: string
  state: string
  country: string
}

export interface StravaTokens {
  access_token: string
  refresh_token: string
  expires_at: number
  athlete: StravaAthlete
}

export interface StravaActivity {
  id: string  // Changed to string for consistency with database storage
  name: string
  type: string
  start_date: string
  distance: number
  moving_time: number
  elapsed_time: number
  total_elevation_gain: number
  start_latlng: [number, number] | null
  end_latlng: [number, number] | null
  location_city?: string
  location_state?: string
  location_country?: string
  timezone?: string
  average_speed?: number
  max_speed?: number
}

class StravaService {
  private clientId: string
  private redirectUri: string

  constructor() {
    this.clientId = import.meta.env.VITE_STRAVA_CLIENT_ID
    this.redirectUri = import.meta.env.VITE_STRAVA_REDIRECT_URI
  }

  // Generate authorization URL for OAuth flow
  getAuthUrl(): string {
    const scope = 'read,activity:read'
    const responseType = 'code'
    const approvalPrompt = 'force'

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: responseType,
      approval_prompt: approvalPrompt,
      scope: scope
    })

    return `https://www.strava.com/oauth/authorize?${params.toString()}`
  }

  // Exchange authorization code for access token via secure backend
  async exchangeToken(code: string, codeVerifier?: string): Promise<StravaTokens> {
    const response = await fetch('/api/strava-exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authorization_code: code,
        code_verifier: codeVerifier
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.details || errorData.error || `Token exchange failed: ${response.statusText}`)
    }

    const tokens = await response.json()
    
    // Store tokens securely
    this.storeTokens(tokens)
    
    return tokens
  }

  // Get athlete's activities
  async getActivities(accessToken: string, page = 1, perPage = 50): Promise<StravaActivity[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    })

    const response = await fetch(`https://www.strava.com/api/v3/athlete/activities?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch activities: ${response.statusText}`)
    }

    const activities = await response.json()
    
    // Convert activity IDs to strings for consistency with database storage
    return activities.map((activity: any) => ({
      ...activity,
      id: String(activity.id)
    }))
  }

  // Get hiking activities only (filtered by type)
  async getHikingActivities(accessToken: string): Promise<StravaActivity[]> {
    const allActivities = await this.getActivities(accessToken, 1, 200)
    // return allActivities.filter(activity => activity.type === 'Hike')
    return allActivities  // For development, return all activities (so it's easier to test multiple activities in one day)
  }

  // Store tokens in localStorage
  storeTokens(tokens: StravaTokens): void {
    localStorage.setItem('daybyDay_strava_tokens', JSON.stringify(tokens))
  }

  // Get stored tokens from localStorage. Does NOT discard expired tokens —
  // an expired access token can still be refreshed via the refresh token.
  getStoredTokens(): StravaTokens | null {
    const stored = localStorage.getItem('daybyDay_strava_tokens')
    if (!stored) return null

    try {
      return JSON.parse(stored) as StravaTokens
    } catch {
      return null
    }
  }

  // Exchange the stored refresh token for a fresh access token via the backend.
  // Strava's refresh response omits the athlete, so we preserve the existing one.
  async refreshTokens(): Promise<StravaTokens> {
    const current = this.getStoredTokens()
    if (!current?.refresh_token) {
      throw new Error('No Strava refresh token available')
    }

    const response = await fetch('/api/strava-refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refresh_token })
    })

    if (!response.ok) {
      // Refresh token is invalid/revoked — only then is re-login actually needed.
      if (response.status === 401) {
        this.clearTokens()
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.details || errorData.error || `Token refresh failed: ${response.statusText}`)
    }

    const refreshed = await response.json()
    const tokens: StravaTokens = {
      access_token: refreshed.access_token,
      refresh_token: refreshed.refresh_token,
      expires_at: refreshed.expires_at,
      athlete: current.athlete
    }
    this.storeTokens(tokens)
    return tokens
  }

  // Get a valid access token, refreshing first if it is expired or about to expire.
  // Returns null only when there are no stored tokens at all.
  async getValidAccessToken(): Promise<string | null> {
    const tokens = this.getStoredTokens()
    if (!tokens) return null

    // Refresh if within 60s of expiry (or already expired).
    if (tokens.expires_at * 1000 - Date.now() < 60_000) {
      const refreshed = await this.refreshTokens()
      return refreshed.access_token
    }
    return tokens.access_token
  }

  // Clear stored tokens
  clearTokens(): void {
    localStorage.removeItem('daybyDay_strava_tokens')
  }

  // Check if currently connected to Strava. We're connected as long as we hold a
  // refresh token, even if the access token has expired (it can be refreshed).
  isConnected(): boolean {
    const tokens = this.getStoredTokens()
    return !!tokens?.refresh_token
  }

  // Get connected athlete info
  getAthlete(): StravaAthlete | null {
    const tokens = this.getStoredTokens()
    return tokens?.athlete || null
  }
}

export const stravaService = new StravaService()